/**
 * AI视频创作平台 - 用户认证API
 * 封装用户注册、登录、获取用户信息等接口
 */

import { post, get, put } from './request';
import type { 
  User, 
  LoginParams, 
  RegisterParams, 
  LoginResponse,
  UpdateUserParams 
} from '@shared/types';

/**
 * 用户注册
 * @param params 注册参数
 * @returns 登录响应（包含用户信息和Token）
 */
export async function register(params: RegisterParams): Promise<LoginResponse> {
  return post<LoginResponse>('/auth/register', params as unknown as Record<string, unknown>);
}

/**
 * 用户登录
 * @param params 登录参数
 * @returns 登录响应（包含用户信息和Token）
 */
export async function login(params: LoginParams): Promise<LoginResponse> {
  return post<LoginResponse>('/auth/login', params as unknown as Record<string, unknown>);
}

/**
 * 刷新Token
 * @param refreshToken 刷新令牌
 * @returns 新的Token信息
 */
export async function refreshToken(refreshToken: string): Promise<{
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}> {
  return post('/auth/refresh', { refreshToken });
}

/**
 * 获取当前用户信息
 * @returns 用户信息
 */
export async function getUserProfile(): Promise<User> {
  return get<User>('/user/profile');
}

/**
 * 更新用户信息
 * @param params 更新参数
 * @returns 更新后的用户信息
 */
export async function updateUserProfile(params: UpdateUserParams): Promise<User> {
  return put<User>('/user/profile', params as unknown as Record<string, unknown>);
}

/**
 * 获取用户积分余额
 * @returns 积分余额
 */
export async function getUserCredits(): Promise<{ credits: number }> {
  return get<{ credits: number }>('/user/credits');
}

/**
 * 获取用户 VIP/会员状态
 * @returns VIP 信息
 */
export async function getUserVipInfo(): Promise<{
  isVip: boolean;
  level?: number;
  name?: string;
  expireTime?: string;
}> {
  return get('/user/vip');
}
